/**
 * Módulo de Gestión de Lista de Asistentes
 * Muestra el historial completo de escaneos y permite exportar a Excel (CSV).
 * Cumple con .idx-rules.md: funciones < 40 líneas, archivo < 300 líneas.
 */

import { getAllScans } from '../infra/db.js';
import { getCurrentUser } from './router.js';

let loadedScans = [];
let filteredScans = [];
let currentPage = 1;
const ITEMS_PER_PAGE = 10;

// Escucha el evento global para recargar la lista de asistentes
window.addEventListener('load_assistants', async () => {
  try {
    const user = getCurrentUser();
    const companyId = user ? user.company_id : null;
    const scans = await getAllScans(companyId);
    loadedScans = scans || [];
    applyFilterAndRender();
  } catch (error) {
    console.error('Error al cargar asistentes:', error);
  }
});

// Filtra y renderiza la tabla según los inputs de fecha
function applyFilterAndRender() {
  const startVal = document.getElementById('assistants-start-date')?.value;
  const endVal = document.getElementById('assistants-end-date')?.value;
  filteredScans = filterScansByLimaDate(loadedScans, startVal, endVal);
  currentPage = 1;
  renderTable();
}

// Renderiza los registros filtrados en la tabla HTML (paginados)
function renderTable() {
  const tbody = document.getElementById('table-assistants-body');
  if (!tbody) return;
  
  if (filteredScans.length === 0) {
    tbody.innerHTML = '<tr><td colspan="7" class="text-center text-muted">No se encontraron escaneos en este rango.</td></tr>';
    updatePaginationUI();
    return;
  }

  const reversed = filteredScans.slice().reverse();
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const pageItems = reversed.slice(startIndex, endIndex);

  tbody.innerHTML = pageItems.map(s => {
    const dateObj = parseDate(s.timestamp);
    const dateStr = dateObj.toLocaleDateString('es-PE');
    const timeStr = dateObj.toLocaleTimeString('es-PE');
    const statusClass = s.status === 'ALLOWED' ? 'badge-gold' : 'badge-red';
    const statusText = s.status === 'ALLOWED' ? 'Permitido' : `Rechazado (${s.rejectionReason || 'N/A'})`;
    
    return `
      <tr>
        <td data-label="Fecha Registro">${dateStr}</td>
        <td data-label="Hora Registro">${timeStr}</td>
        <td data-label="DNI (Hash)" class="font-mono text-xs">${s.hashedDni.substring(0, 15)}...</td>
        <td data-label="Fecha Nac.">${s.birthDate || 'N/A'}</td>
        <td data-label="Sexo">${s.gender === 'M' ? 'Masculino' : s.gender === 'F' ? 'Femenino' : 'Otros'}</td>
        <td data-label="Estado"><span class="badge ${statusClass}">${statusText}</span></td>
        <td data-label="Portero">${s.portero || 'Sistema'}</td>
      </tr>
    `;
  }).join('');

  updatePaginationUI();
}

// Actualiza los elementos visuales de la paginación
function updatePaginationUI() {
  const pagContainer = document.getElementById('assistants-pagination');
  if (!pagContainer) return;

  const totalItems = filteredScans.length;
  if (totalItems === 0) {
    pagContainer.style.display = 'none';
    return;
  }

  pagContainer.style.display = 'flex';
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE) || 1;
  
  if (currentPage > totalPages) currentPage = totalPages;
  if (currentPage < 1) currentPage = 1;

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalItems);

  const infoEl = document.getElementById('assistants-pagination-info');
  if (infoEl) {
    infoEl.innerText = `Mostrando ${startIndex + 1}-${endIndex} de ${totalItems} registros`;
  }

  const indicatorEl = document.getElementById('assistants-page-indicator');
  if (indicatorEl) {
    indicatorEl.innerText = `Pág. ${currentPage} de ${totalPages}`;
  }

  const prevBtn = document.getElementById('btn-assistants-prev');
  const nextBtn = document.getElementById('btn-assistants-next');
  if (prevBtn) prevBtn.disabled = (currentPage === 1);
  if (nextBtn) nextBtn.disabled = (currentPage === totalPages);
}

// Exporta los escaneos mostrados a un archivo CSV compatible con Excel
function exportToCSV(scans) {
  let csvContent = "\uFEFF"; // UTF-8 BOM para soporte de caracteres especiales en Excel
  csvContent += "Fecha Registro,Hora Registro,DNI Hashed,Fecha Nacimiento,Sexo,Estado,Portero\n";

  scans.forEach(s => {
    const dateObj = parseDate(s.timestamp);
    const dateStr = dateObj.toLocaleDateString('es-PE');
    const timeStr = dateObj.toLocaleTimeString('es-PE');
    const gender = s.gender === 'M' ? 'Masculino' : s.gender === 'F' ? 'Femenino' : 'Otros';
    const status = s.status === 'ALLOWED' ? 'Permitido' : `Rechazado (${s.rejectionReason || ''})`;
    
    const row = [
      dateStr,
      timeStr,
      `"${s.hashedDni}"`,
      s.birthDate || 'N/A',
      gender,
      `"${status}"`,
      `"${s.portero || 'Sistema'}"`
    ].join(",");
    csvContent += row + "\n";
  });

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `asistentes_${new Date().toISOString().slice(0, 10)}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Helper para parsear la fecha interpretando UTC de forma segura
function parseDate(timestampStr) {
  if (!timestampStr) return new Date();
  let isoStr = timestampStr.toString().trim();
  if (isoStr.includes(' ')) {
    isoStr = isoStr.replace(' ', 'T');
  }
  if (!isoStr.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(isoStr)) {
    isoStr += 'Z';
  }
  return new Date(isoStr);
}

// Filtra registros usando America/Lima timezone
function filterScansByLimaDate(scans, startDate, endDate) {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Lima',
    year: 'numeric', month: '2-digit', day: '2-digit'
  });
  return scans.filter(s => {
    if (!s.timestamp) return false;
    let dateStr = '';
    try {
      dateStr = formatter.format(parseDate(s.timestamp));
    } catch (e) {
      return false;
    }
    return !(startDate && dateStr < startDate) && !(endDate && dateStr > endDate);
  });
}

/**
 * Inicializa los listeners de filtros, paginación y exportación de asistentes.
 */
export function initAssistantsModule() {
  const startEl = document.getElementById('assistants-start-date');
  const endEl = document.getElementById('assistants-end-date');
  const clearEl = document.getElementById('btn-clear-assistants-filter');
  const exportBtn = document.getElementById('btn-export-assistants');
  const prevBtn = document.getElementById('btn-assistants-prev');
  const nextBtn = document.getElementById('btn-assistants-next');

  startEl?.addEventListener('change', applyFilterAndRender);
  endEl?.addEventListener('change', applyFilterAndRender);
  
  clearEl?.addEventListener('click', () => {
    if (startEl) startEl.value = '';
    if (endEl) endEl.value = '';
    applyFilterAndRender();
  });

  exportBtn?.addEventListener('click', () => {
    exportToCSV(filteredScans);
  });

  prevBtn?.addEventListener('click', () => {
    if (currentPage > 1) {
      currentPage--;
      renderTable();
    }
  });

  nextBtn?.addEventListener('click', () => {
    const totalPages = Math.ceil(filteredScans.length / ITEMS_PER_PAGE) || 1;
    if (currentPage < totalPages) {
      currentPage++;
      renderTable();
    }
  });
}
